import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Fingerprint, KeyRound, Layers, Plus, RotateCcw, Search, Trash2 } from "lucide-react";
import { useToast } from "../../ui/Toast.js";
import { Dialog } from "../../ui/Dialog.js";
import { Field, SelectInput, TextInput } from "../../ui/forms.js";
import { Alert, Avatar, Badge, Button, ConfirmInline, EmptyState, Skeleton } from "../../ui/primitives.js";
import type { OrgDetail, TeamCollection } from "../../api/teams.js";
import { useRestoreTeamItem, useSaveTeamItem, useTeamItems, useToggleTeamFavorite, useTrashTeamItem } from "../../data/teams.js";
import type { TeamEntry } from "../../data/teams.js";
import { formatRelativeTime } from "../../lib/format.js";
import { hasPasskey } from "../../lib/passkeys.js";
import { canWriteItems, describeTeamError } from "../../lib/teams.js";
import { EntryEditor, EntryView, draftFrom, emptyDraft } from "../vault/EntryParts.js";
import type { EntryDraft } from "../vault/EntryParts.js";
import type { VaultPayload } from "../../vaultPayload.js";

interface ItemsTabProps {
  readonly org: OrgDetail;
  readonly collections: ReadonlyArray<TeamCollection> | undefined;
  readonly keyReady: boolean;
}

type Selection =
  | { readonly kind: "view"; readonly id: string }
  | { readonly kind: "edit"; readonly id: string }
  | { readonly kind: "new" };

function collectionName(collections: ReadonlyArray<TeamCollection>, id: string): string {
  return collections.find((c: TeamCollection): boolean => c.id === id)?.name ?? "Unbekannt";
}

export function TeamItemsTab(props: ItemsTabProps): ReactNode {
  const { org } = props;
  const collections = props.collections ?? [];
  const items = useTeamItems(org, props.collections, props.keyReady);
  const [collectionFilter, setCollectionFilter] = useState<string>("all");
  const [query, setQuery] = useState("");
  const [showTrash, setShowTrash] = useState(false);
  const [selection, setSelection] = useState<Selection | undefined>(undefined);
  const writable = canWriteItems(org.role);

  const visible = useMemo((): TeamEntry[] => {
    const needle = query.trim().toLowerCase();
    const entries = items.data?.entries ?? [];
    return entries
      .filter((entry: TeamEntry): boolean => (entry.meta.deletedAt !== null) === showTrash)
      .filter((entry: TeamEntry): boolean => collectionFilter === "all" || entry.meta.collectionId === collectionFilter)
      .filter((entry: TeamEntry): boolean =>
        needle === "" || `${entry.data.title} ${entry.data.username} ${entry.data.url}`.toLowerCase().includes(needle)
      )
      .sort((a: TeamEntry, b: TeamEntry): number => b.meta.updatedAt - a.meta.updatedAt);
  }, [items.data, query, showTrash, collectionFilter]);

  const folders = useMemo((): string[] => {
    const names = new Set((items.data?.entries ?? []).map((entry: TeamEntry): string => entry.meta.folder));
    return [...names].sort((a: string, b: string): number => a.localeCompare(b, "de"));
  }, [items.data]);

  const selected = selection === undefined || selection.kind === "new"
    ? undefined
    : items.data?.entries.find((entry: TeamEntry): boolean => entry.meta.id === selection.id);

  return (
    <section aria-label="Einträge des Teams">
      <div className="flex flex-col gap-2.5 lg:flex-row lg:items-center">
        <div className="relative min-w-0 flex-1">
          <Search size={15} aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-mist-2" />
          <TextInput
            type="search"
            aria-label="Einträge durchsuchen"
            placeholder="Titel, Benutzername oder Adresse suchen"
            value={query}
            onChange={(event): void => setQuery(event.target.value)}
            autoComplete="off"
            className="pl-9"
          />
        </div>
        <div className="w-full lg:w-56">
          <SelectInput aria-label="Sammlung filtern" value={collectionFilter} onChange={(event): void => setCollectionFilter(event.target.value)}>
            <option value="all">Alle Sammlungen</option>
            {collections.map((c: TeamCollection) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </SelectInput>
        </div>
        <Button variant={showTrash ? "secondary" : "ghost"} icon={Trash2} aria-pressed={showTrash} onClick={(): void => setShowTrash((v: boolean): boolean => !v)}>
          {showTrash ? "Aktive Einträge" : "Papierkorb"}
        </Button>
        {writable ? (
          <Button icon={Plus} disabled={collections.length === 0} onClick={(): void => setSelection({ kind: "new" })}>
            Neuer Eintrag
          </Button>
        ) : null}
      </div>

      {props.collections !== undefined && collections.length === 0 ? (
        <div className="mt-4">
          <Alert tone="info">Lege zuerst unter „Sammlungen“ eine Sammlung an. Einträge gehören immer zu einer Sammlung.</Alert>
        </div>
      ) : null}

      {items.data !== undefined && items.data.skipped > 0 ? (
        <div className="mt-4">
          <Alert tone="danger">{items.data.skipped} Einträge konnten nicht entschlüsselt werden. Sie fehlen in dieser Ansicht.</Alert>
        </div>
      ) : null}

      <div className="mt-4">
        {items.isPending || (!props.keyReady && props.collections !== undefined) ? (
          <div className="grid gap-2">
            <Skeleton className="h-14" />
            <Skeleton className="h-14" />
            <Skeleton className="h-14" />
          </div>
        ) : items.isError ? (
          <Alert tone="danger">Einträge konnten nicht geladen werden. {describeTeamError(items.error)}</Alert>
        ) : visible.length === 0 ? (
          <EmptyState icon={KeyRound} title={showTrash ? "Papierkorb ist leer" : "Noch keine Einträge"}>
            {showTrash
              ? "Gelöschte Einträge des Teams erscheinen hier."
              : writable
                ? "Lege den ersten gemeinsamen Eintrag an. Alle Teammitglieder mit Zugriff sehen ihn."
                : "Hier erscheinen die Einträge, sobald jemand sie anlegt."}
          </EmptyState>
        ) : (
          <div className="overflow-hidden rounded-xl border border-line bg-ink-2">
            <div aria-hidden="true" className="hidden items-center gap-4 border-b sm:flex border-line bg-ink/40 px-4 py-2 text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-mist-2">
              <span className="flex-1">Name</span>
              <span className="hidden w-40 sm:block">Sammlung</span>
              <span className="hidden w-28 text-right lg:block">Geändert</span>
            </div>
            <ul className="divide-y divide-line">
              {visible.map((entry: TeamEntry) => (
                <li key={entry.meta.id}>
                  <button
                    type="button"
                    onClick={(): void => setSelection({ kind: "view", id: entry.meta.id })}
                    className="flex w-full items-center gap-4 px-4 py-2.5 text-left transition-colors duration-150 hover:bg-foam/[0.03]"
                  >
                    <span className="flex min-w-0 flex-1 items-center gap-3">
                      <Avatar label={entry.data.title} />
                      <span className="min-w-0">
                        <span className="flex items-center gap-1.5">
                          <span className="truncate text-sm font-medium text-foam">{entry.data.title}</span>
                          {hasPasskey(entry.data) ? <Fingerprint size={12} aria-label="Mit Passkey" className="shrink-0 text-mist" /> : null}
                        </span>
                        <span className="block truncate text-[0.8125rem] text-mist">
                          {entry.data.username !== "" ? entry.data.username : "Kein Benutzername"}
                        </span>
                      </span>
                    </span>
                    <span className="hidden w-40 shrink-0 truncate text-[0.8125rem] text-mist sm:block">{collectionName(collections, entry.meta.collectionId)}</span>
                    <span className="hidden w-28 shrink-0 text-right text-[0.8125rem] text-mist-2 lg:block">{formatRelativeTime(entry.meta.updatedAt, Date.now())}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <Dialog
        open={selection !== undefined}
        onClose={(): void => setSelection(undefined)}
        title={selection?.kind === "new" ? "Neuer Eintrag" : "Eintrag"}
        variant="sheet"
      >
        {selection?.kind === "view" ? (
          <TeamItemView
            org={org}
            collections={collections}
            entry={selected}
            writable={writable}
            onEdit={(): void => {
              if (selected !== undefined) {
                setSelection({ kind: "edit", id: selected.meta.id });
              }
            }}
            onClosed={(): void => setSelection(undefined)}
          />
        ) : null}
        {selection?.kind === "edit" || selection?.kind === "new" ? (
          <TeamItemEditor
            org={org}
            collections={collections}
            folders={folders}
            entry={selection.kind === "edit" ? selected : undefined}
            onSaved={(id: string): void => setSelection({ kind: "view", id })}
            onCancel={(): void => setSelection(selection.kind === "edit" ? { kind: "view", id: selection.id } : undefined)}
          />
        ) : null}
      </Dialog>
    </section>
  );
}

function TeamItemView(props: {
  readonly org: OrgDetail;
  readonly collections: ReadonlyArray<TeamCollection>;
  readonly entry: TeamEntry | undefined;
  readonly writable: boolean;
  readonly onEdit: () => void;
  readonly onClosed: () => void;
}): ReactNode {
  const toast = useToast();
  const toggleFavorite = useToggleTeamFavorite(props.org.id);
  const trash = useTrashTeamItem(props.org.id);
  const restore = useRestoreTeamItem(props.org.id);
  const [confirming, setConfirming] = useState(false);
  const entry = props.entry;

  if (entry === undefined) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-10 text-center">
        <p className="text-base font-semibold text-foam">Eintrag nicht gefunden</p>
        <p className="text-sm text-mist">Er wurde gelöscht oder liegt nicht in diesem Team.</p>
      </div>
    );
  }

  const trashed = entry.meta.deletedAt !== null;

  function moveToTrash(): void {
    if (entry === undefined) {
      return;
    }
    trash.mutate(
      { collectionId: entry.meta.collectionId, itemId: entry.meta.id },
      {
        onSuccess: (): void => {
          props.onClosed();
          toast.notify("In den Papierkorb verschoben.", { tone: "success" });
        },
        onError: (err: Error): void => {
          toast.notify(describeTeamError(err), { tone: "danger" });
          setConfirming(false);
        }
      }
    );
  }

  function restoreItem(): void {
    if (entry === undefined) {
      return;
    }
    restore.mutate(
      { collectionId: entry.meta.collectionId, itemId: entry.meta.id },
      {
        onSuccess: (): void => toast.notify("Eintrag wiederhergestellt.", { tone: "success" }),
        onError: (err: Error): void => toast.notify(describeTeamError(err), { tone: "danger" })
      }
    );
  }

  let footer: ReactNode = null;
  if (props.writable && trashed) {
    footer = (
      <Button variant="secondary" icon={RotateCcw} loading={restore.isPending} className="self-start" onClick={restoreItem}>
        Wiederherstellen
      </Button>
    );
  } else if (props.writable) {
    footer = confirming ? (
      <ConfirmInline
        message="Eintrag in den Papierkorb verschieben?"
        confirmLabel="Verschieben"
        pending={trash.isPending}
        onConfirm={moveToTrash}
        onCancel={(): void => setConfirming(false)}
      />
    ) : (
      <Button variant="ghost" size="sm" icon={Trash2} className="self-start text-buoy hover:bg-buoy/10 hover:text-buoy" onClick={(): void => setConfirming(true)}>
        In den Papierkorb
      </Button>
    );
  }

  const collection = collectionName(props.collections, entry.meta.collectionId);
  return (
    <EntryView
      key={entry.meta.id}
      data={entry.data}
      folder={entry.meta.folder === collection ? "" : entry.meta.folder}
      favorite={entry.meta.favorite}
      version={entry.meta.version}
      updatedAt={entry.meta.updatedAt}
      onEdit={props.writable && !trashed ? props.onEdit : undefined}
      onToggleFavorite={props.writable && !trashed ? (): void => toggleFavorite.mutate(entry) : undefined}
      favoritePending={toggleFavorite.isPending}
      badge={<Badge tone="neutral" icon={Layers}>{collection}</Badge>}
      footer={footer}
    />
  );
}

function TeamItemEditor(props: {
  readonly org: OrgDetail;
  readonly collections: ReadonlyArray<TeamCollection>;
  readonly folders: ReadonlyArray<string>;
  readonly entry: TeamEntry | undefined;
  readonly onSaved: (id: string) => void;
  readonly onCancel: () => void;
}): ReactNode {
  const save = useSaveTeamItem(props.org);
  const toast = useToast();
  const [serverError, setServerError] = useState<string | undefined>(undefined);
  const [collectionId, setCollectionId] = useState<string>(props.entry?.meta.collectionId ?? props.collections[0]?.id ?? "");
  const entry = props.entry;
  const isNew = entry === undefined;
  const initial: EntryDraft = entry !== undefined ? draftFrom(entry.data, entry.meta.folder, entry.meta.favorite) : emptyDraft(undefined);

  function onSubmit(payload: VaultPayload, draft: EntryDraft): void {
    setServerError(undefined);
    save.mutate(
      {
        id: entry?.meta.id,
        version: entry?.meta.version,
        collectionId,
        folder: draft.folder,
        favorite: draft.favorite,
        payload
      },
      {
        onSuccess: (saved: TeamEntry): void => {
          toast.notify(isNew ? "Eintrag angelegt." : "Änderungen gespeichert.", { tone: "success" });
          props.onSaved(saved.meta.id);
        },
        onError: (err: Error): void => setServerError(describeTeamError(err))
      }
    );
  }

  const leading = isNew ? (
    <Field id="team-item-collection" label="Sammlung">
      <SelectInput id="team-item-collection" value={collectionId} onChange={(event): void => setCollectionId(event.target.value)}>
        {props.collections.map((c: TeamCollection) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </SelectInput>
    </Field>
  ) : (
    <div className="flex items-center gap-2 text-sm text-mist">
      Sammlung: <Badge tone="neutral">{collectionName(props.collections, collectionId)}</Badge>
    </div>
  );

  return (
    <EntryEditor
      key={entry?.meta.id ?? "new"}
      mode={isNew ? "new" : "edit"}
      initial={initial}
      folders={props.folders}
      saving={save.isPending}
      serverError={serverError}
      leading={leading}
      onSubmit={onSubmit}
      onCancel={props.onCancel}
    />
  );
}
